import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../common/Header';
import { IoSearch } from "react-icons/io5";
import { LuPackage } from "react-icons/lu";
import { GrDeliver } from "react-icons/gr";
import { GrInstall } from "react-icons/gr";
import { BiSupport } from "react-icons/bi";
import { useLanguage } from '../../store/languageContext';
import translate from '../../utils/translation';


const HowItWorks = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const sectionRef = useRef(null);
    const { language, setNavColor } = useLanguage();

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setNavColor('black');
                }
            },
            {
                threshold: 0.1,
                rootMargin: '-50px 0px -20% 0px',
            }
        );

        const currentRef = sectionRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [setNavColor]);
    const steps = [
        {
            number: '01',
            title: translate[language]['howStepTitle1'],
            header: translate[language]['howStepHeader1'],
            text: translate[language]['howStepText1'],
            icon: <IoSearch />,
        },
        {
            number: '02',
            title: translate[language]['howStepTitle2'],
            header: translate[language]['howStepHeader2'],
            text: translate[language]['howStepText2'],
            icon: <LuPackage />,
        },
        {
            number: '03',
            title: translate[language]['howStepTitle3'],
            header: translate[language]['howStepHeader3'],
            text: translate[language]['howStepText3'],
            icon: <GrDeliver />,
        },
        {
            number: '04',
            title: translate[language]['howStepTitle4'],
            header: translate[language]['howStepHeader4'],
            text: translate[language]['howStepText4'],
            icon: <GrInstall />,
        },
        {
            number: '05',
            title: translate[language]['howStepTitle5'],
            header: translate[language]['howStepHeader5'],
            text: translate[language]['howStepText5'],
            icon: <BiSupport />,
        },
    ];

    return (
        <section id="how-it-works" ref={sectionRef} className="container mx-auto landing-section pb-[50px] flex flex-col">
            <Header text={translate[language]['howHeader']} />
            <div className="flex">
                <div className='w-1/3' />
                <div className="md:w-2/3 flex flex-col justify-between items-center">
                    <div className='w-1/2'>
                        <motion.h2
                            initial={{ y: 100, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
                            viewport={{ once: true }}
                            className='text-[60px] leading-[60px] font-semibold mb-4'>
                            {translate[language]['howTitle1']}
                        </motion.h2>
                        <motion.p
                            initial={{ y: 50, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.4, ease: 'easeOut' }}
                            viewport={{ once: true }}
                            className='opacity-[0.5]'>
                            {translate[language]['howTitle2']}
                        </motion.p>
                    </div>
                </div>
            </div>
            <div className="flex gap-4 mt-8 flex-1">
                {steps.map((step, index) => (
                    <motion.div
                        initial={{ y: 100, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.5, delay: (index + 1) * 0.2, ease: 'easeOut' }}
                        viewport={{ once: true }}
                        key={index}
                        className={`p-4  flex flex-col items-start justify-between cursor-pointer ${activeIndex === index ? 'w-[calc(100%/6*2)] bg-[#589EEE]' : 'w-[calc(100%/6)] bg-[#F7F7F7]'
                            }`}
                        onClick={() => setActiveIndex(index)}
                    >
                        <span className={`${activeIndex === index ? 'text-[#fff]' : 'text-[#444]'} text-sm font-semibold uppercase opacity-75`}>
                            {step.number}. {step.title}
                        </span>
                        <div className='relative'>
                            <div className={`duration-500 relative text-[#fff] mb-4`}>
                                <div className={`text-[40px] absolute mb-3 duration-500 ${activeIndex === index ? 'text-[#fff] bottom-full scale-125' : 'text-[#589EEE] bottom-0 '}`}>
                                    {step.icon}
                                </div>
                                <div className={`text-nowrap duration-500 ${activeIndex === index ? 'translate-x-0 opacity-100' : 'translate-x-1/2 opacity-0'}`}>
                                    <h3 className="text-xl font-semibold uppercase">{step.header}</h3>
                                    <p className="text-sm mt-2">{step.text}</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </section >
    );
};

export { HowItWorks };